import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { Artifacts, TabsPanel, Loader } from 'ui';
import { EntitiesTable, InstrumentsTable } from 'modules/common';
import { CreditReviewEntities } from 'modules/creditReview';
import {
  searchEntitiesAction,
  searchInstrumentsAction,
  searchRatingNewsAction,
  searchResearchAction,
} from 'store/searchResult/searchResultActions';
import { lastVisitedRatingTypeAction } from 'store/shared/lastVisited/lastVisitedActions';
import { useArtifacts } from 'shared/hooks';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { titleByRatingType } from 'shared/constants/titleByRatingType';
import { getSearchableRegions, getSearchbleIndustries } from 'selectors/common';
import {
  getInitialSearchResultsFilters,
  getInitialSearchResultsModalFilters,
} from 'shared/functions/searchFilters/searchFilters';
import { useQuery } from 'shared/hooks/useQuery/useQuery';
import {
  searchEntitiesFiltersParser,
  searchNewsQueryParser,
  searchNewsFiltersParser,
} from 'shared/parsers';
import { useCofaceContract } from 'shared/hooks/useCofaceContract/useCofaceContract';
import { useFeatures } from 'shared/hooks/useFeatures/useFeatures';
import { isIsin } from 'utils';

const Research = ({ query, filters }) => {
  const { research, isRatingRequestPending } = useSelector(({ searchResult }) => searchResult);

  const {
    limit,
    handleChangeArtifactsPage,
    handleArtifactClick,
    currentPage,
    handleChangeRowsPerPage,
  } = useArtifacts({
    filters,
    query,
    getArtifactsAction: searchResearchAction,
    customLimit: 10,
  });

  return (
    <Artifacts
      data={research.data}
      isPending={research.pending || isRatingRequestPending}
      total={research.total}
      limit={limit}
      onItemClick={handleArtifactClick}
      onChangePage={handleChangeArtifactsPage}
      currentPage={currentPage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
    />
  );
};

Research.defaultProps = {
  query: '',
  filters: {},
};

Research.propTypes = {
  query: PropTypes.string,
  filters: PropTypes.object,
};

const RatingNews = ({ query, filters }) => {
  const { ratingNews, isRatingRequestPending } = useSelector(({ searchResult }) => searchResult);

  const {
    limit,
    handleChangeArtifactsPage,
    handleArtifactClick,
    currentPage,
    handleChangeRowsPerPage,
  } = useArtifacts({
    filters,
    query,
    getArtifactsAction: searchRatingNewsAction,
    customLimit: 10,
  });

  return (
    <Artifacts
      data={ratingNews.data}
      isPending={ratingNews.pending || isRatingRequestPending}
      total={ratingNews.total}
      limit={limit}
      onItemClick={handleArtifactClick}
      onChangePage={handleChangeArtifactsPage}
      currentPage={currentPage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
    />
  );
};

RatingNews.defaultProps = {
  query: '',
  filters: {},
};

RatingNews.propTypes = {
  query: PropTypes.string,
  filters: PropTypes.object,
};

export const SearchResult = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryParams = useQuery();
  const dispatch = useDispatch();
  const lastVisitedRatingType = useSelector(({ shared }) => shared.lastVisited.ratingType);
  const { isSearchStarted, entities, instruments, ratingNews, research } = useSelector(
    ({ searchResult }) => searchResult,
  );
  const { userId } = useSelector(({ auth }) => auth.data);
  const features = useFeatures();
  const { cofaceAccessType, isPending: cofaceContractInfoPending } = useCofaceContract();

  const { data: regions } = useSelector(getSearchableRegions);
  const industries = useSelector(getSearchbleIndustries);

  const { query, ratingType = lastVisitedRatingType, module, ...filtersParams } = queryParams;

  const [initialFilters, setInitialFilters] = useState({});
  const [initialModalFilters, setInitialModalFilters] = useState({});

  const [loading, setLoading] = useState(true);
  const [companyModuleChangePending, setCompanyModuleChangePending] = useState(false);

  const isEsg = !module || module === 'esg';
  const isCredit = !module || module === 'credit';

  useEffect(() => {
    if (ratingType !== lastVisitedRatingType) {
      dispatch(lastVisitedRatingTypeAction({ ratingType }));
    }
  }, [ratingType]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCompanyModuleChange = (_, value) => {
    if (ratingType !== value) {
      setCompanyModuleChangePending(true);
      navigate({
        pathname: `/search-result`,
        search: qs.stringify(
          { ...queryParams, ratingType: value, query, module },
          { addQueryPrefix: true },
        ),
      });
    }
  };

  useDeepCompareEffect(() => {
    if (!regions.length || !industries.length) return;

    const modalFilters = getInitialSearchResultsModalFilters(
      ratingType,
      filtersParams,
      regions,
      industries,
    );
    const filters = getInitialSearchResultsFilters(ratingType, modalFilters);

    setInitialFilters(filters);
    setInitialModalFilters(modalFilters);

    setCompanyModuleChangePending(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions, industries, queryParams]);

  useEffect(() => {
    setLoading(Boolean(!regions.length || !industries.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions, industries]);

  if (loading || companyModuleChangePending || cofaceContractInfoPending) {
    return (
      <Box padding={5} textAlign="center">
        <Loader />
      </Box>
    );
  }

  const getCreditRating = () => (
    <TabsPanel active={isIsin(query) ? 'instruments' : 'entities'} lazy={false}>
      <div
        label={t('Entities')}
        value="entities"
        counter={entities.byRatingType[ratingTypes.CREDIT_RATING].total}
      >
        <EntitiesTable
          dataSelector={entities.byRatingType[ratingTypes.CREDIT_RATING]}
          actionCreator={searchEntitiesAction}
          actionCreatorParams={{ query, ratingType: ratingTypes.CREDIT_RATING }}
          searchQuery={queryParams}
          initialFilter={initialFilters}
          initialFilterModalValues={initialModalFilters}
          renderDatatable={!isSearchStarted}
        />
      </div>
      <div label={t('Instruments')} value="instruments" counter={instruments.total}>
        <InstrumentsTable
          dataSelector={instruments}
          actionCreator={searchInstrumentsAction}
          actionCreatorParams={{ query }}
          searchQuery={query}
          initialFilter={initialFilters}
          initialFilterModalValues={initialModalFilters}
          renderDatatable={!isSearchStarted}
        />
      </div>
      <div label={t('Research')} value="research" counter={research.total}>
        <Box p={2}>
          <Research
            query={searchNewsQueryParser(query, initialFilters, regions)}
            filters={searchNewsFiltersParser(initialFilters)}
          />
        </Box>
      </div>
      <div label={t('Rating News')} value="rating-news" counter={ratingNews.total}>
        <Box p={2}>
          <RatingNews
            query={searchNewsQueryParser(query, initialFilters, regions)}
            filters={searchNewsFiltersParser(initialFilters)}
          />
        </Box>
      </div>
    </TabsPanel>
  );

  const getCreditReview = () => (
    <TabsPanel active="entities" lazy={false}>
      <div
        label={t('ENTITIES')}
        value="entities"
        counter={entities.byRatingType[ratingTypes.CREDIT_REVIEW].total}
      >
        <CreditReviewEntities
          dataSelector={entities.byRatingType[ratingTypes.CREDIT_REVIEW]}
          actionCreator={searchEntitiesAction}
          actionCreatorParams={{
            userId,
            query,
            ratingType: ratingTypes.CREDIT_REVIEW,
          }}
          searchQuery={queryParams}
          initialFilter={initialFilters}
          initialFilterModalValues={initialModalFilters}
          cofaceAccessType={cofaceAccessType}
        />
      </div>
    </TabsPanel>
  );

  const getEsg = () => (
    <TabsPanel active="entities" lazy={false}>
      <div
        label={t('Entities')}
        value="entities"
        counter={entities.byRatingType[ratingTypes.ESG_IMPACT].total}
      >
        <EntitiesTable
          dataSelector={entities.byRatingType[ratingTypes.ESG_IMPACT]}
          actionCreator={searchEntitiesAction}
          actionCreatorParams={{ query, ratingType: ratingTypes.ESG_IMPACT }}
          searchQuery={queryParams}
          ratingType={ratingTypes.ESG_IMPACT}
          initialFilter={searchEntitiesFiltersParser(initialFilters, industries)}
          initialFilterModalValues={initialModalFilters}
        />
      </div>
    </TabsPanel>
  );

  const getSentiment = () => (
    <TabsPanel active="entities" lazy={false}>
      <div
        label={t('Entities')}
        value="entities"
        counter={entities.byRatingType[ratingTypes.SENTIMENT].total}
      >
        <EntitiesTable
          dataSelector={entities.byRatingType[ratingTypes.SENTIMENT]}
          actionCreator={searchEntitiesAction}
          actionCreatorParams={{ query, ratingType: ratingTypes.SENTIMENT }}
          searchQuery={queryParams}
          ratingType={ratingTypes.SENTIMENT}
          initialFilter={searchEntitiesFiltersParser(initialFilters, industries)}
          initialFilterModalValues={initialModalFilters}
        />
      </div>
    </TabsPanel>
  );

  const getCst = () => (
    <TabsPanel active="entities" lazy={false}>
      <div
        label={t('Entities')}
        value="entities"
        counter={entities.byRatingType[ratingTypes.CLIMATE_STRESS_TEST].total}
      >
        <EntitiesTable
          dataSelector={entities.byRatingType[ratingTypes.CLIMATE_STRESS_TEST]}
          actionCreator={searchEntitiesAction}
          actionCreatorParams={{ query, ratingType: ratingTypes.CLIMATE_STRESS_TEST }}
          searchQuery={queryParams}
          ratingType={ratingTypes.CLIMATE_STRESS_TEST}
          initialFilter={initialFilters}
          initialFilterModalValues={initialModalFilters}
        />
      </div>
    </TabsPanel>
  );

  return (
    <>
      <Box mb={2} mt={2}>
        <Typography variant="h4">Search results for: {query}</Typography>
      </Box>
      {module === 'credit' && getCreditRating()}
      {module !== 'credit' && (
        <TabsPanel variant="chip" active={ratingType} onChange={onCompanyModuleChange}>
          {isCredit && (
            <div
              label={titleByRatingType[ratingTypes.CREDIT_RATING]}
              value={ratingTypes.CREDIT_RATING}
            >
              {ratingType === ratingTypes.CREDIT_RATING && getCreditRating()}
            </div>
          )}
          {isCredit && features.creditReview && (
            <div
              label={titleByRatingType[ratingTypes.CREDIT_REVIEW]}
              value={ratingTypes.CREDIT_REVIEW}
            >
              {ratingType === ratingTypes.CREDIT_REVIEW && getCreditReview()}
            </div>
          )}
          {isEsg && (
            <div label={titleByRatingType[ratingTypes.ESG_IMPACT]} value={ratingTypes.ESG_IMPACT}>
              {ratingType === ratingTypes.ESG_IMPACT && getEsg()}
            </div>
          )}
          {isEsg && features.esgSentiment && (
            <div label={titleByRatingType[ratingTypes.SENTIMENT]} value={ratingTypes.SENTIMENT}>
              {ratingType === ratingTypes.SENTIMENT && getSentiment()}
            </div>
          )}
          {isEsg && (
            <div
              label={titleByRatingType[ratingTypes.CLIMATE_STRESS_TEST]}
              value={ratingTypes.CLIMATE_STRESS_TEST}
            >
              {ratingType === ratingTypes.CLIMATE_STRESS_TEST && getCst()}
            </div>
          )}
        </TabsPanel>
      )}
    </>
  );
};
